import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  getMeetingsAttendance,
  getMeetingsByGroup,
  getExplorationBySchool,
  getExplorationsByFacilitator,
  getSocializationBySchool,
  getSchools,
  getUsers,
  updateIncidenceActionEvidenceReview,
  updateMeetingEvidenceReview
} from '../../API/apiService';
import { toast } from 'react-toastify';
import Loader from '../../components/loader';
import PdfIcon from '../../assets/icons/pdfIcon.svg';
import EvidencePreviewModal from '../../components/evidencePreviewModal';
import { buildProtectedImageUrl } from '../../utils/secureMedia';
import { formatDateInBogota } from '../../utils/dateTime';
import { resolveEvidenceSlot } from '../../utils/evidenceSlot';
import './style.scss';

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getSchoolName(school) {
  return (
    school?.name ||
    school?.code ||
    school?.institutionName ||
    'Institución sin nombre'
  );
}

function getGroupName(group) {
  if (group?.institutional) return 'Evidencias institucionales';
  return group?.code || group?.name || `Grupo ${group?.id}`;
}

function getCharacterizedPeopleLabel(group) {
  const count = Number(
    group?.characterizedPeopleCount ?? group?.characterized_people_count ?? 0
  );
  return `${count} ${count === 1 ? 'persona caracterizada' : 'personas caracterizadas'}`;
}

function formatSessionDate(value) {
  const rawValue = String(value || '').trim();
  const calendarDateMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  const calendarDate = calendarDateMatch ? calendarDateMatch[1] : value;
  return formatDateInBogota(calendarDate, 'es-CO') || 'Fecha pendiente';
}

function getMeetingEvidenceSlot(media, fallbackSlot) {
  return resolveEvidenceSlot(media, fallbackSlot, 3);
}

const MEETING_EVIDENCE_LABELS = [
  'Evidencia fotográfica',
  'Acta/guía',
  'Listado de asistencia'
];

function normalizeInstitutionalEvidence(record, type, order = 1) {
  if (!record?.meetingGroupId) return null;
  return {
    ...record,
    id: record.meetingGroupId,
    order,
    meeting_date: record.meetingDate,
    meetingImages: record.images || [],
    evidenceType: type,
    evidenceLabel: type === 'concertacion' ? 'Concertación' : `Socialización ${order}`,
    isInstitutional: true
  };
}

async function getInstitutionalEvidence(schoolId) {
  const [concertation, socialization1, socialization2] = await Promise.all([
    getExplorationBySchool(schoolId),
    getSocializationBySchool(schoolId, 1),
    getSocializationBySchool(schoolId, 2)
  ]);
  return [
    normalizeInstitutionalEvidence(concertation, 'concertacion', 1),
    normalizeInstitutionalEvidence(socialization1, 'socializacion', 1),
    normalizeInstitutionalEvidence(socialization2, 'socializacion', 2)
  ].filter(Boolean);
}

async function getReviewerSchools(mediatorId) {
  const [schoolResponse, concertations] = await Promise.all([
    getSchools(Number(mediatorId)),
    getExplorationsByFacilitator(Number(mediatorId))
  ]);
  const schoolsWithConcertation = new Set(
    (Array.isArray(concertations) ? concertations : []).map(record => Number(record.schoolId))
  );
  return (Array.isArray(schoolResponse) ? schoolResponse : []).filter(
    school => school?.active !== false || school?.hasInstitutionalEvidence === true ||
      school?.has_institutional_evidence === true || schoolsWithConcertation.has(Number(school.id))
  );
}

function ReviewerEvidenceBrowser() {
  const location = useLocation();
  const restoredState = location.state?.reviewerEvidenceState;
  const evidenceTarget = location.state?.evidenceTarget;
  const [mediators, setMediators] = useState([]);
  const [schools, setSchools] = useState(restoredState?.schools || []);
  const [sessions, setSessions] = useState(restoredState?.sessions || []);
  const [selectedMediator, setSelectedMediator] = useState(
    restoredState?.selectedMediator || null
  );
  const [selectedSchool, setSelectedSchool] = useState(
    restoredState?.selectedSchool || null
  );
  const [selectedGroup, setSelectedGroup] = useState(
    restoredState?.selectedGroup || null
  );
  const [selectedSession, setSelectedSession] = useState(
    restoredState?.selectedSession || null
  );
  const [attendanceDetail, setAttendanceDetail] = useState(
    restoredState?.attendanceDetail || null
  );
  const [search, setSearch] = useState(restoredState?.search || '');
  const [roleFilter, setRoleFilter] = useState(restoredState?.roleFilter || '');
  const [showRoleFilter, setShowRoleFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadMediators() {
      setLoading(true);
      const users = await getUsers({ noCache: true });
      if (!mounted) return;

      const activeMediators = (Array.isArray(users) ? users : [])
        .filter(user => {
          const roleName = normalizeText(user?.role?.name);
          return user?.active !== false && roleName.includes('mediador');
        })
        .sort((a, b) =>
          String(a?.full_name || '').localeCompare(
            String(b?.full_name || ''),
            'es'
          )
        );

      setMediators(activeMediators);

      if (evidenceTarget) {
        const targetMediator = activeMediators.find(
          mediator => Number(mediator.id) === Number(evidenceTarget.mediatorId)
        );
        if (targetMediator) {
          setSelectedMediator(targetMediator);
          const schoolResponse = await getReviewerSchools(Number(targetMediator.id));
          if (!mounted) return;
          const mediatorSchools = Array.isArray(schoolResponse) ? schoolResponse : [];
          setSchools(mediatorSchools);

          const targetSchool = evidenceTarget.schoolId
            ? mediatorSchools.find(school => Number(school.id) === Number(evidenceTarget.schoolId))
            : mediatorSchools.find(school =>
            (school?.groups || []).some(
              group => Number(group.id) === Number(evidenceTarget.groupId)
            )
          );
          if (targetSchool && evidenceTarget.evidenceType && evidenceTarget.evidenceType !== 'sesion') {
            setSelectedSchool(targetSchool);
            setSelectedGroup({id: `institution-${targetSchool.id}`, institutional: true});
            const institutionalSessions = await getInstitutionalEvidence(targetSchool.id);
            if (!mounted) return;
            setSessions(institutionalSessions);
            setSelectedSession(institutionalSessions.find(
              item => Number(item.id) === Number(evidenceTarget.meetingId)
            ) || null);
            setAttendanceDetail({attendances: []});
            setLoading(false);
            return;
          }
          const targetGroup = targetSchool?.groups?.find(
            group => Number(group.id) === Number(evidenceTarget.groupId)
          );
          if (targetSchool && targetGroup) {
            setSelectedSchool(targetSchool);
            setSelectedGroup(targetGroup);
            const meetingResponse = await getMeetingsByGroup(targetGroup.id);
            if (!mounted) return;
            const groupSessions = (Array.isArray(meetingResponse) ? meetingResponse : [])
              .filter(session => session?.type === 'MEETING')
              .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
            setSessions(groupSessions);
            const targetSession = groupSessions.find(
              session => Number(session.id) === Number(evidenceTarget.meetingId)
            );
            if (targetSession) {
              setSelectedSession(targetSession);
              const detail = await getMeetingsAttendance(targetSession.id);
              if (!mounted) return;
              setAttendanceDetail(detail || { attendances: [] });
            }
          }
        }
      }
      setLoading(false);
    }

    loadMediators();
    return () => {
      mounted = false;
    };
  }, [evidenceTarget]);

  const filteredMediators = useMemo(() => {
    const term = normalizeText(search);
    return mediators.filter(mediator => {
      const roleName = String(mediator?.role?.name || '').trim();
      const matchesRole = !roleFilter || roleName === roleFilter;
      const matchesSearch =
        !term ||
        normalizeText(
          `${mediator?.full_name} ${mediator?.document} ${roleName}`
        ).includes(term);
      return matchesRole && matchesSearch;
    });
  }, [mediators, search, roleFilter]);

  const mediatorRoleTypes = useMemo(
    () =>
      Array.from(
        new Set(
          mediators
            .map(mediator => String(mediator?.role?.name || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, 'es')),
    [mediators]
  );

  async function selectMediator(mediator) {
    setSelectedMediator(mediator);
    setSelectedSchool(null);
    setSelectedGroup(null);
    setSelectedSession(null);
    setAttendanceDetail(null);
    setSessions([]);
    setLoadingDetail(true);

    const response = await getReviewerSchools(Number(mediator.id));
    const mediatorSchools = Array.isArray(response) ? response : [];
    setSchools(mediatorSchools);
    setLoadingDetail(false);
  }

  function selectSchool(school) {
    setSelectedSchool(school);
    setSelectedGroup(null);
    setSelectedSession(null);
    setAttendanceDetail(null);
    setSessions([]);
  }

  async function selectGroup(group) {
    setSelectedGroup(group);
    setSelectedSession(null);
    setAttendanceDetail(null);
    setSessions([]);
    setLoadingDetail(true);
    if (group.institutional) {
      setSessions(await getInstitutionalEvidence(selectedSchool.id));
      setLoadingDetail(false);
      return;
    }
    const response = await getMeetingsByGroup(group.id);
    const groupSessions = (Array.isArray(response) ? response : [])
      .filter(session => session?.type === 'MEETING')
      .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
    setSessions(groupSessions);
    setLoadingDetail(false);
  }

  async function selectSession(session) {
    setSelectedSession(session);
    setAttendanceDetail(null);
    setLoadingDetail(true);
    if (session.isInstitutional) {
      setAttendanceDetail({ attendances: [] });
      setLoadingDetail(false);
      return;
    }
    const response = await getMeetingsAttendance(session.id);
    setAttendanceDetail(response || { attendances: [] });
    setLoadingDetail(false);
  }

  function updateReviewedSession(updatedMeeting) {
    if (!updatedMeeting) return;
    setSelectedSession(current =>
      current?.id === updatedMeeting.id ? {...current, ...updatedMeeting} : current
    );
    setSessions(current =>
      current.map(session =>
        session.id === updatedMeeting.id ? {...session, ...updatedMeeting} : session
      )
    );
  }

  function resetTo(level) {
    if (level === 'mediators') {
      setSelectedMediator(null);
      setSchools([]);
    }
    if (level === 'schools') setSelectedSchool(null);
    if (level === 'groups') setSelectedGroup(null);
    setSelectedSession(null);
    setAttendanceDetail(null);
    setSessions([]);
  }

  const groups = Array.isArray(selectedSchool?.groups)
    ? [{id: `institution-${selectedSchool.id}`, institutional: true}, ...selectedSchool.groups]
    : [];

  return (
    <section className='reviewer-evidence'>
      <header className='reviewer-evidence__topbar'>
        <Link to='/revisor' className='reviewer-evidence__back'>
          <span className='material-symbols-rounded'>arrow_back</span>
          Centro de revisión
        </Link>
        <div>
          <span className='material-symbols-rounded'>fact_check</span>
          <span>Evidencias</span>
        </div>
      </header>

      <div className='reviewer-evidence__shell'>
        <div className='reviewer-evidence__heading'>
          <div>
            <span className='reviewer-evidence__eyebrow'>CONSULTA JERÁRQUICA</span>
            <h1>Explorador de evidencias</h1>
            <p>
              Navega por mediador, institución, grupo y sesión para consultar
              los soportes registrados.
            </p>
          </div>
          <span className='reviewer-evidence__read-only'>
            <span className='material-symbols-rounded'>fact_check</span>
            Revisión y seguimiento
          </span>
        </div>

        <nav className='reviewer-evidence__crumbs' aria-label='Ruta actual'>
          <button type='button' onClick={() => resetTo('mediators')}>
            Mediadores
          </button>
          {selectedMediator && (
            <>
              <span>/</span>
              <button type='button' onClick={() => resetTo('schools')}>
                {selectedMediator.full_name}
              </button>
            </>
          )}
          {selectedSchool && (
            <>
              <span>/</span>
              <button type='button' onClick={() => resetTo('groups')}>
                {getSchoolName(selectedSchool)}
              </button>
            </>
          )}
          {selectedGroup && (
            <>
              <span>/</span>
              <strong>{getGroupName(selectedGroup)}</strong>
            </>
          )}
        </nav>

        {loading ? (
          <div className='reviewer-evidence__loading'><Loader /></div>
        ) : (
          <div className='reviewer-evidence__workspace'>
            <aside className='reviewer-evidence__mediators'>
              <div className='reviewer-evidence__search-row'>
                <label>
                  <span className='material-symbols-rounded'>search</span>
                  <input
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder='Buscar mediador'
                  />
                </label>
                <button
                  type='button'
                  className={roleFilter ? 'is-active' : ''}
                  aria-label='Filtrar por tipo de mediador'
                  aria-expanded={showRoleFilter}
                  title='Filtrar por tipo de mediador'
                  onClick={() => setShowRoleFilter(current => !current)}>
                  <span className='material-symbols-rounded'>filter_list</span>
                  {roleFilter && <span className='reviewer-evidence__filter-dot' />}
                </button>
              </div>
              {showRoleFilter && (
                <div className='reviewer-evidence__role-filter'>
                  <div>
                    <strong>Tipo de mediador</strong>
                    {roleFilter && (
                      <button
                        type='button'
                        onClick={() => setRoleFilter('')}>
                        Limpiar
                      </button>
                    )}
                  </div>
                  <button
                    type='button'
                    className={!roleFilter ? 'is-selected' : ''}
                    onClick={() => {
                      setRoleFilter('');
                      setShowRoleFilter(false);
                    }}>
                    Todos
                    <span>{mediators.length}</span>
                  </button>
                  {mediatorRoleTypes.map(roleName => (
                    <button
                      type='button'
                      key={roleName}
                      className={roleFilter === roleName ? 'is-selected' : ''}
                      onClick={() => {
                        setRoleFilter(roleName);
                        setShowRoleFilter(false);
                      }}>
                      {roleName}
                      <span>
                        {
                          mediators.filter(
                            mediator => mediator?.role?.name === roleName
                          ).length
                        }
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className='reviewer-evidence__aside-title'>
                <span>
                  {roleFilter || 'Mediadores activos'}
                </span>
                <strong>{filteredMediators.length}</strong>
              </div>
              <div className='reviewer-evidence__mediator-list'>
                {filteredMediators.map(mediator => (
                  <button
                    type='button'
                    key={mediator.id}
                    className={
                      selectedMediator?.id === mediator.id ? 'is-selected' : ''
                    }
                    onClick={() => selectMediator(mediator)}>
                    <span className='reviewer-evidence__avatar'>
                      {String(mediator?.full_name || 'M').charAt(0)}
                    </span>
                    <span>
                      <strong>{mediator.full_name}</strong>
                      <small>{mediator.role?.name}</small>
                    </span>
                    <span className='material-symbols-rounded'>chevron_right</span>
                  </button>
                ))}
                {filteredMediators.length === 0 && (
                  <p className='reviewer-evidence__empty-small'>
                    No se encontraron mediadores activos.
                  </p>
                )}
              </div>
            </aside>

            <main className='reviewer-evidence__results'>
              {loadingDetail ? (
                <div className='reviewer-evidence__loading'><Loader /></div>
              ) : !selectedMediator ? (
                <EmptyState
                  icon='person_search'
                  title='Selecciona un mediador'
                  description='Verás sus instituciones y procesos formativos activos.'
                />
              ) : !selectedSchool ? (
                <Collection
                  eyebrow='INSTITUCIONES / PF'
                  title={selectedMediator.full_name}
                  count={schools.length}
                  empty='Este mediador no tiene instituciones o PF activos.'
                  items={schools}
                  renderItem={school => (
                    <button
                      type='button'
                      key={school.id}
                      className='reviewer-evidence__entity-card'
                      onClick={() => selectSchool(school)}>
                      <span className='reviewer-evidence__entity-icon'>
                        <span className='material-symbols-rounded'>school</span>
                      </span>
                      <span>
                        <strong>{getSchoolName(school)}</strong>
                        <small>
                          {school?.municipality?.name || 'Ubicación sin registrar'}
                          {' · '}
                          {(school?.groups || []).length} grupos
                        </small>
                      </span>
                      <span className='material-symbols-rounded'>arrow_forward</span>
                    </button>
                  )}
                />
              ) : !selectedGroup ? (
                <Collection
                  eyebrow='GRUPOS'
                  title={getSchoolName(selectedSchool)}
                  count={groups.length}
                  empty='Esta institución o PF todavía no tiene grupos.'
                  items={groups}
                  renderItem={group => (
                    <button
                      type='button'
                      key={group.id}
                      className='reviewer-evidence__entity-card'
                      onClick={() => selectGroup(group)}>
                      <span className='reviewer-evidence__entity-icon reviewer-evidence__entity-icon--group'>
                        <span className='material-symbols-rounded'>groups</span>
                      </span>
                      <span>
                        <strong>{getGroupName(group)}</strong>
                        <small>
                          {group.institutional
                            ? 'Concertación y socialización'
                            : (group?.ageRange || group?.age_range || 'Rango sin definir')}
                          {' · '}
                          {group.institutional ? 'Nivel institución' : (group?.status || 'Sin estado')}
                        </small>
                      </span>
                      <span className='material-symbols-rounded'>arrow_forward</span>
                    </button>
                  )}
                />
              ) : (
                <Sessions
                  group={selectedGroup}
                  sessions={sessions}
                  selectedSession={selectedSession}
                  attendanceDetail={attendanceDetail}
                  onSelect={selectSession}
                  onReviewUpdated={updateReviewedSession}
                  returnState={{
                    selectedMediator,
                    selectedSchool,
                    selectedGroup,
                    selectedSession,
                    attendanceDetail,
                    schools,
                    sessions,
                    search,
                    roleFilter
                  }}
                />
              )}
            </main>
          </div>
        )}
      </div>
    </section>
  );
}

function getIncidenceEvidenceLabel(image, index) {
  const labels = {
    bitacora: 'Bitácora',
    ficha_metodologica: 'Ficha metodológica',
    asistencia: 'Listado de asistencia'
  };
  return labels[String(image?.name || '').trim().toLowerCase()] || `Evidencia ${index + 1}`;
}

function isPreviewableIncidenceEvidence(image) {
  return ['pdf', 'png', 'jpg', 'jpeg', 'webp'].includes(
    String(image?.type || '').trim().toLowerCase()
  );
}

export function IncidenceEvidenceCollection({ items, targetActionId, onReviewUpdated }) {
  const [preview, setPreview] = useState(null);
  const selectedAction = preview ? items.find(item => item.id === preview.actionId) : null;
  const selectedImage = selectedAction?.images?.[preview?.imageIndex] || null;

  useEffect(() => {
    if (!targetActionId) return;
    window.setTimeout(() => {
      document.getElementById(`incidence-action-${targetActionId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 0);
  }, [targetActionId, items]);

  return (
    <section className='reviewer-evidence__incidence'>
      <EvidencePreviewModal
        open={Boolean(selectedImage)}
        sourceUrl={selectedImage ? buildProtectedImageUrl(selectedImage.id, false) : ''}
        fileType={normalizeText(selectedImage?.type) === 'pdf' ? 'pdf' : 'image'}
        fileName={selectedImage
          ? `${getIncidenceEvidenceLabel(selectedImage, preview.imageIndex)} · ${selectedAction?.meetingName || 'Acción de incidencia'}`
          : ''}
        currentIndex={preview?.imageIndex ?? 0}
        totalItems={selectedAction?.images?.length || 0}
        onClose={() => setPreview(null)}
        closeOnBackdrop
      />
      <header>
        <div>
          <span className='reviewer-evidence__eyebrow'>ACCIONES DE INCIDENCIA</span>
          <h2>Evidencias cargadas</h2>
          <p>Consulta las bitácoras, fichas metodológicas y asistencias disponibles.</p>
        </div>
        <span>{items.length}</span>
      </header>
      {items.length > 0 ? (
        <div className='reviewer-evidence__incidence-list'>
          {items.map(action => (
            <article
              id={`incidence-action-${action.id}`}
              key={`incidence-${action.id}`}
              className={Number(targetActionId) === Number(action.id) ? 'is-targeted' : ''}>
              <div>
                <strong>{action.meetingName || 'Acción de incidencia'}</strong>
                <small>{formatSessionDate(action.meetingDate)} · {action.groupCode || 'Sin grupo'}</small>
                <small>{action.mediatorName || 'Sin mediador'}</small>
              </div>
              <div className='reviewer-evidence__incidence-files'>
                {(action.images || []).map((image, index) => {
                  const label = getIncidenceEvidenceLabel(image, index);
                  const imageUrl = buildProtectedImageUrl(image.id, false);
                  return isPreviewableIncidenceEvidence(image) ? (
                    <button
                      type='button'
                      key={image.id}
                      onClick={() => setPreview({ actionId: action.id, imageIndex: index })}
                    >
                      <span className='material-symbols-rounded'>visibility</span>
                      {label}
                    </button>
                  ) : (
                    <a key={image.id} href={imageUrl} download>
                      <span className='material-symbols-rounded'>download</span>
                      {label}
                    </a>
                  );
                })}
              </div>
              <EvidenceReviewPanel session={action} onReviewUpdated={onReviewUpdated} />
            </article>
          ))}
        </div>
      ) : (
        <p className='reviewer-evidence__empty-small'>
          Aún no hay evidencias de acciones de incidencia cargadas.
        </p>
      )}
    </section>
  );
}

function EmptyState({ icon, title, description }) {
  return (
    <div className='reviewer-evidence__empty'>
      <span className='material-symbols-rounded'>{icon}</span>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function Collection({ eyebrow, title, count, empty, items, renderItem }) {
  return (
    <section className='reviewer-evidence__collection'>
      <header>
        <div>
          <span className='reviewer-evidence__eyebrow'>{eyebrow}</span>
          <h2>{title}</h2>
        </div>
        <span>{count}</span>
      </header>
      <div className='reviewer-evidence__entity-list'>
        {items.length ? items.map(renderItem) : (
          <EmptyState icon='folder_off' title='Sin registros' description={empty} />
        )}
      </div>
    </section>
  );
}

function Sessions({
  group,
  sessions,
  selectedSession,
  attendanceDetail,
  onSelect,
  onReviewUpdated,
  returnState
}) {
  const [previewIndex, setPreviewIndex] = useState(null);
  const evidenceItems = selectedSession?.meetingImages || [];
  const categorizedEvidenceItems = evidenceItems.map((media, index) => {
    const slot = getMeetingEvidenceSlot(media, index + 1);
    return {
      media,
      index,
      slot,
      label: MEETING_EVIDENCE_LABELS[slot - 1] || `Evidencia ${index + 1}`
    };
  });
  const standardEvidenceItems = categorizedEvidenceItems.filter(
    evidence => evidence.slot < 4
  );
  const displayedStandardEvidenceItems = standardEvidenceItems;
  const previewEvidence =
    Number.isInteger(previewIndex) ? evidenceItems[previewIndex] : null;
  const previewItem = categorizedEvidenceItems.find(
    evidence => evidence.index === previewIndex
  );

  useEffect(() => {
    setPreviewIndex(null);
  }, [selectedSession?.id]);

  function movePreview(direction) {
    if (!evidenceItems.length) return;
    setPreviewIndex(current => {
      const currentIndex = Number.isInteger(current) ? current : 0;
      return (currentIndex + direction + evidenceItems.length) % evidenceItems.length;
    });
  }

  return (
    <section className='reviewer-evidence__collection'>
      <EvidencePreviewModal
        open={Boolean(previewEvidence)}
        sourceUrl={
          previewEvidence
            ? buildProtectedImageUrl(previewEvidence.id, false)
            : ''
        }
        fileType={
          normalizeText(previewEvidence?.type) === 'pdf' ? 'pdf' : 'image'
        }
        fileName={
          previewEvidence
            ? `${previewItem?.label || `Evidencia ${previewIndex + 1}`} · ${selectedSession?.evidenceLabel || `Sesión ${selectedSession?.order}`}`
            : ''
        }
        currentIndex={previewIndex}
        totalItems={evidenceItems.length}
        onPrevious={() => movePreview(-1)}
        onNext={() => movePreview(1)}
        onClose={() => setPreviewIndex(null)}
        closeOnBackdrop
      />
      <header>
        <div>
          <span className='reviewer-evidence__eyebrow'>{group.institutional ? 'INSTITUCIÓN' : 'SESIONES'}</span>
          <h2>
            {getGroupName(group)}
            {!group.institutional && ` - ${getCharacterizedPeopleLabel(group)}`}
          </h2>
        </div>
        <span>{sessions.length}</span>
      </header>
      {sessions.length === 0 ? (
        <EmptyState
          icon='event_busy'
          title='Sin sesiones'
          description='Este grupo todavía no tiene sesiones registradas.'
        />
      ) : (
        <div className='reviewer-evidence__session-layout'>
          <div className='reviewer-evidence__session-list'>
            {sessions.map(session => {
              const evidenceCount = session?.meetingImages?.length || 0;
              return (
                <button
                  type='button'
                  key={session.id}
                  className={selectedSession?.id === session.id ? 'is-selected' : ''}
                  onClick={() => onSelect(session)}>
                  <span className='reviewer-evidence__session-order'>
                    {session.isInstitutional ? (session.evidenceType === 'concertacion' ? 'C' : `S${session.order}`) : session.order}
                  </span>
                  <span>
                    <strong>{session.evidenceLabel || `Sesión ${session.order}`}</strong>
                    <small>{formatSessionDate(session.meeting_date)}</small>
                  </span>
                  <span className={evidenceCount ? 'has-evidence' : ''}>
                    {evidenceCount} {evidenceCount === 1 ? 'soporte' : 'soportes'}
                  </span>
                </button>
              );
            })}
          </div>

          <div className='reviewer-evidence__session-detail'>
            {!selectedSession ? (
              <EmptyState
                icon='touch_app'
                title='Selecciona una sesión'
                description='Aquí podrás ver su información y evidencias.'
              />
            ) : (
              <>
                <div className='reviewer-evidence__detail-heading'>
                  <div>
                    <span className='reviewer-evidence__eyebrow'>DETALLE</span>
                    <h3>{selectedSession.evidenceLabel || `Sesión ${selectedSession.order}`}</h3>
                  </div>
                  <span>{formatSessionDate(selectedSession.meeting_date)}</span>
                </div>
                {!selectedSession.isInstitutional && Number(selectedSession.signedAttendanceCount || 0) > 0 && (
                  <Link
                    className='reviewer-evidence__official-list'
                    to={`/revisor/asistencia?evento_id=${selectedSession.id}`}
                    state={{
                      backTo: {
                        pathname: '/revisor/evidencias',
                        state: { reviewerEvidenceState: returnState }
                      },
                      backLabel: 'Volver a evidencias'
                    }}>
                    <span className='material-symbols-rounded'>description</span>
                    <span>
                      <strong>Ver listado oficial de asistencia</strong>
                      <small>Documento generado con las firmas registradas</small>
                    </span>
                    <span className='material-symbols-rounded'>open_in_new</span>
                  </Link>
                )}
                <dl>
                  <div>
                    <dt>Objetivo</dt>
                    <dd>{selectedSession.goal || 'Sin objetivo registrado'}</dd>
                  </div>
                  {!selectedSession.isInstitutional && <div>
                    <dt>Horario</dt>
                    <dd>
                      {selectedSession.start_time || '—'} a{' '}
                      {selectedSession.end_time || '—'}
                    </dd>
                  </div>}
                  {!selectedSession.isInstitutional && <div>
                    <dt>Asistencias firmadas</dt>
                    <dd>{Number(selectedSession.signedAttendanceCount || 0)}</dd>
                  </div>}
                  <div className='reviewer-evidence__mediator-observations'>
                    <dt>Observaciones del mediador</dt>
                    <dd>
                      {selectedSession.notes ||
                        selectedSession.meetingNotes ||
                        selectedSession.meeting_notes ||
                        'Sin observaciones registradas'}
                    </dd>
                  </div>
                </dl>
                <EvidenceReviewPanel
                  session={selectedSession}
                  onReviewUpdated={onReviewUpdated}
                />
                <div className='reviewer-evidence__evidence-heading'>
                  <strong>Evidencias</strong>
                  <span>{displayedStandardEvidenceItems.length}</span>
                </div>
                <div className='reviewer-evidence__evidence-grid'>
                  {displayedStandardEvidenceItems.map(({ media, index, label }) => {
                    const isPdf = normalizeText(media?.type) === 'pdf';
                    return (
                      <button
                        type='button'
                        onClick={() => setPreviewIndex(index)}
                        key={media?.id || index}>
                        <img
                          src={isPdf ? PdfIcon : buildProtectedImageUrl(media?.id, true)}
                          alt={isPdf ? `${label}, documento PDF` : label}
                        />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                  {displayedStandardEvidenceItems.length === 0 && (
                    <p>Esta sesión no tiene evidencias cargadas.</p>
                  )}
                </div>
                {!selectedSession.isInstitutional && <AttendanceChecklist
                  attendances={attendanceDetail?.attendances || []}
                />}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function EvidenceReviewPanel({ session, onReviewUpdated }) {
  const storedStatus = session?.reviewStatus || session?.review_status || 'pending';
  const storedObservations =
    session?.reviewObservations || session?.review_observations || '';
  const [status, setStatus] = useState(storedStatus);
  const [observations, setObservations] = useState(storedObservations);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(storedStatus);
    setObservations(storedObservations);
  }, [session?.id, storedStatus, storedObservations]);

  async function saveReview() {
    if (status === 'pending') {
      toast('Selecciona Aprobado o Requiere subsanación.', { type: 'warning' });
      return;
    }
    if (status === 'needs_correction' && !observations.trim()) {
      toast('Escribe qué debe corregir el mediador.', { type: 'warning' });
      return;
    }

    setSaving(true);
    const updateReview = session?.sourceType === 'incidence_action'
      ? updateIncidenceActionEvidenceReview
      : updateMeetingEvidenceReview;
    const updatedMeeting = await updateReview(session.id, {
      status,
      observations: status === 'needs_correction' ? observations.trim() : ''
    });
    setSaving(false);

    if (!updatedMeeting) return;
    onReviewUpdated(updatedMeeting);
    toast('Revisión guardada correctamente.', { type: 'success' });
  }

  const statusLabel = status === 'approved'
    ? 'Aprobado'
    : status === 'needs_correction'
      ? 'Requiere subsanación'
      : 'Pendiente de revisión';

  return (
    <section className='reviewer-evidence__review-panel'>
      <header>
        <div>
          <span className='material-symbols-rounded'>fact_check</span>
          <div>
            <strong>Revisión de evidencias</strong>
            <small>
              Valida la información y los soportes de {session?.sourceType === 'incidence_action'
                ? 'esta acción de incidencia'
                : 'esta sesión'}.
            </small>
          </div>
        </div>
        <span className={`reviewer-evidence__review-status is-${status}`}>
          {statusLabel}
        </span>
      </header>

      <div className='reviewer-evidence__review-options'>
        <button
          type='button'
          className={status === 'approved' ? 'is-selected is-approved' : ''}
          onClick={() => setStatus('approved')}>
          <span className='material-symbols-rounded'>check_circle</span>
          <span>
            <strong>Aprobado</strong>
            <small>La información y las evidencias son correctas.</small>
          </span>
        </button>
        <button
          type='button'
          className={status === 'needs_correction' ? 'is-selected is-correction' : ''}
          onClick={() => setStatus('needs_correction')}>
          <span className='material-symbols-rounded'>rate_review</span>
          <span>
            <strong>Requiere subsanación</strong>
            <small>El mediador debe realizar correcciones.</small>
          </span>
        </button>
      </div>

      {status === 'needs_correction' && (
        <label className='reviewer-evidence__review-observations'>
          <span>Observación para el mediador <strong>*</strong></span>
          <textarea
            value={observations}
            onChange={event => setObservations(event.target.value)}
            maxLength={2000}
            rows={4}
            placeholder='Describe claramente qué debe corregir o reemplazar...'
          />
          <small>{observations.length}/2000</small>
        </label>
      )}

      <div className='reviewer-evidence__review-actions'>
        <button type='button' onClick={saveReview} disabled={saving}>
          <span className='material-symbols-rounded'>save</span>
          {saving ? 'Guardando...' : 'Guardar revisión'}
        </button>
      </div>
    </section>
  );
}

function AttendanceChecklist({ attendances }) {
  const [filter, setFilter] = useState('all');
  const normalizedAttendances = Array.isArray(attendances) ? attendances : [];
  const attended = normalizedAttendances.filter(item => item?.attended === true);
  const absent = normalizedAttendances.filter(item => item?.attended !== true);
  const visibleRows =
    filter === 'attended' ? attended : filter === 'absent' ? absent : normalizedAttendances;

  return (
    <section className='reviewer-evidence__checklist'>
      <div className='reviewer-evidence__checklist-heading'>
        <div>
          <strong>Checklist de participantes</strong>
          <small>{normalizedAttendances.length} personas registradas</small>
        </div>
        <div className='reviewer-evidence__attendance-summary'>
          <span className='is-attended'>{attended.length} asistieron</span>
          <span className='is-absent'>{absent.length} no asistieron</span>
        </div>
      </div>

      <div className='reviewer-evidence__checklist-tabs'>
        <button
          type='button'
          className={filter === 'all' ? 'is-active' : ''}
          onClick={() => setFilter('all')}>
          Todos
        </button>
        <button
          type='button'
          className={filter === 'attended' ? 'is-active' : ''}
          onClick={() => setFilter('attended')}>
          Asistieron
        </button>
        <button
          type='button'
          className={filter === 'absent' ? 'is-active' : ''}
          onClick={() => setFilter('absent')}>
          No asistieron
        </button>
      </div>

      <div className='reviewer-evidence__checklist-list'>
        {visibleRows.map((attendance, index) => {
          const person = attendance?.beneficiary || {};
          const didAttend = attendance?.attended === true;
          return (
            <div key={attendance?.id || `${person?.id}-${index}`}>
              <span
                className={`material-symbols-rounded ${
                  didAttend ? 'is-attended' : 'is-absent'
                }`}>
                {didAttend ? 'check_circle' : 'cancel'}
              </span>
              <span>
                <strong>{person?.full_name || 'Persona sin nombre'}</strong>
                <small>
                  {person?.document_type || 'Documento'}{' '}
                  {person?.document || 'sin registrar'}
                </small>
              </span>
              <span className={didAttend ? 'is-attended' : 'is-absent'}>
                {didAttend ? 'Asistió' : 'No asistió'}
              </span>
              {!didAttend && (attendance?.argument || attendance?.notes) && (
                <p>{attendance.argument || attendance.notes}</p>
              )}
            </div>
          );
        })}
        {visibleRows.length === 0 && (
          <p className='reviewer-evidence__checklist-empty'>
            No hay personas en esta categoría.
          </p>
        )}
      </div>
    </section>
  );
}

export default ReviewerEvidenceBrowser;
